import { useState, useEffect } from 'react';
import './App.css';
import Headeradmin from '../components/header-admin';
import ImageGrid from '../components/ImageGrid';
import Modal from '../components/Modal';
import RegistorUser from '../components/RegistorUser';
import UploadForm from '../components/UploadForm';
import CoverManager from '../components/CoverManager';
import ImageManager from '../components/ImageManager';
import UserManager from '../components/admin/UserManager';
import PasswordChange from '../components/admin/PasswordChange';
import SiteCustomizer from '../components/admin/SiteCustomizer';
import SourceManager from '../components/admin/SourceManager';
import NewsManager from '../components/admin/NewsManager';
import FooterEditor from '../components/admin/FooterEditor';
import MigrationManager from '../components/admin/MigrationManager';
import SectionManager from '../components/admin/SectionManager';
import AIManager from '../components/admin/AIManager';
import UserStatsPanel from '../components/admin/UserStatsPanel';
import FeedbackManager from '../components/admin/FeedbackManager';
import { AuthProvider, useAuth } from '../contexts/AuthContext';
import Footer from '../components/footer';

// التبويبات مع الصلاحيات المطلوبة
const TABS = [
  { key: 'upload', label: 'رفع الصور', permission: 'upload', icon: '📤' },
  { key: 'manage', label: 'إدارة الصور', permission: 'delete', icon: '🗂' },
  { key: 'editUI', label: 'تعديل الواجهة', permission: 'editUI', icon: '🎨' },
  { key: 'covers', label: 'أغلفة الأقسام', permission: 'manageCovers', icon: '🖼' },
  { key: 'users', label: 'المستخدمين', permission: 'manageUsers', icon: '👥' },
  { key: 'password', label: 'كلمة المرور', permission: 'changePassword', icon: '🔑' },
  { key: 'source', label: 'مصادر الصور', permission: 'changeSource', icon: '☁' },
  { key: 'news', label: 'الأخبار', permission: 'manageNews', icon: '📰' },
  { key: 'insights', label: 'الإحصائيات والتعليقات', permission: 'manageNews', icon: '📊' },
  { key: 'footer', label: 'الفوتر والمعلومات', permission: 'editFooter', icon: '📝' },
  { key: 'migration', label: 'نقل الصور', permission: 'changeSource', icon: '🔄' },
  { key: 'sections', label: 'إدارة الأقسام', permission: 'editUI', icon: '📂' },
  { key: 'ai', label: 'الذكاء الاصطناعي', permission: 'editUI', icon: '🤖' },
];

function AdminContent() {
  const { user, userProfile, loading, hasPermission, logout } = useAuth();
  const [selectedImg, setSelectedImg] = useState(null);
  const [activeTab, setActiveTab] = useState('');
  const canOpenInsightsTab = hasPermission('manageNews') || hasPermission('manageUsers');

  // تعيين أول تبويب متاح تلقائياً
  useEffect(() => {
    if (userProfile) {
      const firstAvailable = TABS.find((t) => {
        if (t.key === 'insights') return canOpenInsightsTab;
        return hasPermission(t.permission);
      });
      if (firstAvailable) setActiveTab(firstAvailable.key);
    }
  }, [userProfile, hasPermission, canOpenInsightsTab]);

  if (loading) {
    return <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>;
  }

  if (!user) {
    return <RegistorUser />;
  }

  // مستخدم مسجل دخول لكن ليس لديه ملف صلاحيات
  if (!userProfile) {
    return (
      <div className="text-center py-5">
        <h3>⛔ غير مصرح</h3>
        <p>هذا الحساب غير مسجل في نظام الإدارة. تواصل مع المدير.</p>
        <button className="btn btn-outline-danger" onClick={logout}>تسجيل الخروج</button>
      </div>
    );
  }

  const availableTabs = TABS.filter((t) => {
    if (t.key === 'insights') return canOpenInsightsTab;
    return hasPermission(t.permission);
  });

  if (availableTabs.length === 0) {
    return (
      <div className="text-center py-5">
        <h3>⛔ لا توجد صلاحيات</h3>
        <p>لم يتم منحك أي صلاحيات بعد. تواصل مع المدير.</p>
        <button className="btn btn-outline-danger" onClick={logout}>تسجيل الخروج</button>
      </div>
    );
  }

  return (
    <>
      <Headeradmin />
      <div className='container text-center' style={{ paddingTop: 15 }}>
        {/* اسم المستخدم */}
        <div className="mb-2">
          <small className="text-muted">مرحباً، {userProfile.displayName || userProfile.email}</small>
        </div>

        {/* التبويبات */}
        <div className="d-flex justify-content-center flex-wrap gap-1 mb-3">
          {availableTabs.map(tab => (
            <button key={tab.key}
              className={`btn btn-sm ${activeTab === tab.key ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setActiveTab(tab.key)}>
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {/* محتوى التبويبات */}
        {activeTab === 'upload' && hasPermission('upload') && (
          <>
            <UploadForm />
            <ImageGrid setSelectedImg={setSelectedImg} />
            {selectedImg && <Modal selectedImg={selectedImg} setSelectedImg={setSelectedImg} />}
          </>
        )}

        {activeTab === 'manage' && hasPermission('delete') && (
          <ImageManager />
        )}

        {activeTab === 'editUI' && hasPermission('editUI') && (
          <SiteCustomizer />
        )}

        {activeTab === 'covers' && hasPermission('manageCovers') && (
          <CoverManager />
        )}

        {activeTab === 'users' && hasPermission('manageUsers') && (
          <UserManager />
        )}

        {activeTab === 'password' && hasPermission('changePassword') && (
          <PasswordChange />
        )}

        {activeTab === 'source' && hasPermission('changeSource') && (
          <SourceManager />
        )}

        {activeTab === 'news' && hasPermission('manageNews') && (
          <NewsManager />
        )}

        {activeTab === 'insights' && canOpenInsightsTab && (
          <>
            {hasPermission('manageUsers') && <UserStatsPanel />}
            {hasPermission('manageNews') && <FeedbackManager />}
          </>
        )}

        {activeTab === 'footer' && hasPermission('editFooter') && (
          <FooterEditor />
        )}

        {activeTab === 'migration' && hasPermission('changeSource') && (
          <MigrationManager />
        )}

        {activeTab === 'sections' && hasPermission('editUI') && (
          <SectionManager />
        )}

        {activeTab === 'ai' && hasPermission('editUI') && (
          <AIManager />
        )}
      </div>
      <Footer />
    </>
  );
}

function Admin() {
  return (
    <AuthProvider>
      <AdminContent />
    </AuthProvider>
  );
}

export default Admin;
